import { NavArrowRight } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResourceCardLogo } from 'components/blocks/resource-card';
import { useTheme } from 'components/core/theme-provider';
import { AgentCardProviderIcons } from 'pages/agents/components/agent-card-provider-icons';
import { useConfig } from 'hooks/use-config';
import { useGetAgents } from 'features/chat/hooks/use-agents';
import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { cn } from 'lib/utils';

const SUGGESTION_LIMIT = 3;

// Figma node 9764:13461 is 345×135. 345px is not a Tailwind spacing step.
const suggestionCardFrameClassName =
  'h-[135px] w-full lg:w-[32%] max-w-[345px] shrink-0 rounded-field border border-grey-50 bg-grey-50 dark:border-border dark:bg-card';

function agentByline(agent: AgentSummary, userId?: string, userName?: string): string {
  if (agent.created_by && userId && agent.created_by === userId && userName) return userName;
  return 'Klax';
}

function AgentSuggestionCard({
  agent,
  byline,
  onSelect,
}: {
  agent: AgentSummary;
  byline: string;
  onSelect: (agentId: string) => void;
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const titleTextClassName = isDark ? 'text-grey-100' : 'text-grey-900';
  const bodyTextClassName = isDark ? 'text-grey-300' : 'text-grey-900';
  const name = agent.name?.trim() || t('UNTITLED_AGENT', { defaultValue: 'Untitled Agent' });

  return (
    <button
      type="button"
      onClick={() => onSelect(agent.id)}
      className={cn(
        'flex flex-col gap-3 self-start overflow-hidden p-4 text-left',
        suggestionCardFrameClassName
      )}
    >
      <span className="flex w-full items-start gap-2">
        <ResourceCardLogo
          src={agent.logo_url}
          name={name}
          className={cn(
            'size-8 rounded-field bg-white-25 p-0.5 text-lg leading-normal dark:bg-grey-900',
            titleTextClassName
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className={cn('truncate text-sm font-semibold leading-heading', titleTextClassName)}>
            {name}
          </span>
          <span className={cn('truncate text-sm font-normal leading-body', bodyTextClassName)}>
            {agent.description}
          </span>
        </span>
      </span>
      <span className="h-px w-full shrink-0 bg-grey-100 dark:bg-border" />
      <span className="flex h-9 w-full shrink-0 items-center justify-between gap-2">
        <AgentCardProviderIcons
          stacked
          tools={agent.tools}
          enableTools={agent.enable_tools}
          hasKnowledge={agent.has_knowledge}
          hasSkills={agent.has_skills}
          hasSubagents={agent.has_subagents}
          knowledgeCount={agent.folder_ids?.length ?? agent.kb_ids?.length}
          skillCount={agent.skill_ids?.length}
          subagentCount={agent.subagent_ids?.length}
        />
        <span
          className={cn(
            'ml-auto max-w-44 shrink-0 truncate text-right text-sm font-normal leading-body',
            bodyTextClassName
          )}
        >
          {t('BY_AUTHOR', { name: byline, defaultValue: `By ${byline}` })}
        </span>
      </span>
    </button>
  );
}

// `showDefaultAgents` lists Klax's platform-managed agents (new chat landing) instead of the user's own.
export function CreateAgentSuggestions({ showDefaultAgents = false }: { showDefaultAgents?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { configurationSettings } = useConfig();
  const user = useCommonStore((state) => state.user);
  const userId = user?.id;
  const projectKey = configurationSettings?.blocksKey || '';
  const headingTextClassName = theme === 'dark' ? 'text-grey-100' : 'text-grey-900';
  const { data, isLoading } = useGetAgents(
    {
      limit: SUGGESTION_LIMIT,
      offset: 0,
      project_key: projectKey,
      show_hidden_agent: false,
      ...(showDefaultAgents
        ? { platform_managed: true }
        : { created_by: userId ? [userId] : [] }),
    },
    {
      enabled: !!projectKey && (showDefaultAgents || !!userId),
      scope: showDefaultAgents ? 'default-agent-suggestions' : 'create-agent-suggestions',
    },
  );

  const agents = (data?.agents ?? []).slice(0, SUGGESTION_LIMIT);
  if (!isLoading && agents.length === 0) return null;

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  const openAgent = (agentId: string) => {
    navigate(`/chat?agentId=${encodeURIComponent(agentId)}`);
  };

  return (
    <section
      data-testid="create-agent-suggestions"
      aria-label={t('AGENTS_FOR_YOU', { defaultValue: 'Agents for you' })}
      className="mt-16 flex w-full max-w-[785px] shrink-0 flex-col gap-2 border-b border-grey-100 pb-8 dark:border-grey-700 2xl:max-w-[1060px] 2xl:gap-3"
    >
      <div className="flex min-h-10 w-full min-w-0 flex-wrap items-center justify-between gap-2">
        <h2 className={cn('text-sm font-semibold leading-heading', headingTextClassName)}>
          {t('AGENTS_FOR_YOU', { defaultValue: 'Agents for you' })}
        </h2>
        <button
          type="button"
          onClick={() => navigate('/agents')}
          className={cn('flex items-center text-sm font-semibold leading-heading', headingTextClassName)}
        >
          {t('VIEW_ALL_AGENTS', { defaultValue: 'View all agents' })}
          <NavArrowRight width={16} height={16} strokeWidth={1.5} />
        </button>
      </div>
      <div
        data-testid="create-agent-suggestions-scroll"
        className="flex w-full min-w-0 flex-wrap items-start gap-2"
      >
        {isLoading
          ? Array.from({ length: SUGGESTION_LIMIT }, (_, index) => (
              <div
                key={index}
                data-testid="create-agent-suggestion-skeleton"
                className={cn('animate-pulse', suggestionCardFrameClassName)}
              />
            ))
          : agents.map((agent) => (
              <AgentSuggestionCard
                key={agent.id}
                agent={agent}
                byline={agentByline(agent, userId, userName)}
                onSelect={openAgent}
              />
            ))}
      </div>
    </section>
  );
}
